import { ElementRef, Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionService } from './permission.service';
import { AuthService } from '../auth/auth.service';
import { Permission } from '../models/enums/permission.enum';
import { TourStepId } from '../models/enums/tour-step-id.enum';
import { TourPopoverPosition } from '../models/types/tour-popover-position.type';

interface TourStep {
  title: string;
  text: string;
  elementRef: ElementRef<HTMLElement>;
  popoverPosition: TourPopoverPosition;
}

interface RegisteredStep {
  elementRef: ElementRef<HTMLElement>;
  popoverPosition: TourPopoverPosition;
}

interface StepPrepare {
  showDelay?: number;
  beforeShow: () => void;
  afterHide?: () => void;
}

interface StepConfig {
  name: TourStepId;
  title: string;
  text: string;
  position: TourPopoverPosition;
  permissions?: Permission[];
}

const TOUR_STEPS: readonly StepConfig[] = [
  {
    name: TourStepId.Welcome,
    title: 'خوش آمدید!',
    text: 'به پورتال موسسه‌های نیکوکاری خوش آمدید! این تور کوتاه شما را با بخش‌های اصلی آشنا می‌کند. برای ادامه «بعدی» را بزنید.',
    position: 'bottom',
  },
  {
    name: TourStepId.NotificationsBell,
    title: 'اعلان‌ها و پیام‌ها',
    text: 'با انتخاب زنگوله، آخرین اعلان‌ها و پیام‌های افرا را مشاهده کنید.',
    position: 'bottom',
  },
  {
    name: TourStepId.UserProfile,
    title: 'پروفایل کاربری',
    text: 'اینجا می‌توانید اطلاعات پروفایل، نام سازمان و جزئیات تماس خود را ببینید.',
    position: 'bottom',
  },
  {
    name: TourStepId.Sidebar,
    title: 'منوی اصلی',
    text: 'این منوی اصلی است. از اینجا می‌توانید به تمام بخش‌های پورتال دسترسی پیدا کنید.',
    position: 'left',
  },
  {
    name: TourStepId.OrganSelector,
    title: 'انتخاب سازمان',
    text: 'اگر در بیش از یک سازمان عضویت دارید، از اینجا بین آن‌ها جابجا شوید.',
    position: 'left',
  },
  {
    name: TourStepId.TileSelfAssessment,
    title: 'ابزار خود‌ارزیابی',
    text: 'ابزار خود‌ارزیابی به شما امکان می‌دهد وضعیت سازمان خود را بر اساس استاندارد افرا بسنجید و راهکارهای پیشرفت دریافت کنید.',
    position: 'top',
  },
  {
    name: TourStepId.TileWorkshops,
    title: 'کارگاه‌های آموزشی',
    text: 'کارگاه‌های مدیریتی رایگان (حضوری و آنلاین) بر اساس استاندارد افرا را اینجا ببینید.',
    position: 'top',
  },
  {
    name: TourStepId.TileEvaluation,
    title: 'ارزیابی و اعتماد',
    text: 'از اینجا فرآیند ارزیابی را آغاز کنید تا گواهی اعتماد افرا را دریافت نمایید.',
    position: 'top',
  },
  {
    name: TourStepId.TileAfraMagazine,
    title: 'مجله افرا',
    text: 'آخرین اخبار و مقالات مجله افرا را اینجا بخوانید. با انتخاب «مشاهده بیشتر» به صفحه مجله دسترسی پیدا کنید.',
    position: 'top',
  },
  {
    name: TourStepId.Calendar,
    title: 'تقویم',
    text: 'تقویم، رویدادها و تاریخ‌های مهم سازمان شما را نمایش می‌دهد.',
    position: 'top',
    permissions: [Permission.ViewAfraCalendar],
  },
  {
    name: TourStepId.PortalGuide,
    title: 'راهنمای پورتال',
    text: 'هر زمان نیاز به راهنمایی داشتید، از اینجا می‌توانید این تور را دوباره مشاهده کنید.',
    position: 'left',
  },
  {
    name: TourStepId.SupportLogout,
    title: 'پشتیبانی و خروج',
    text: 'نیاز به کمک دارید؟ از اینجا با پشتیبانی تماس بگیرید. همچنین می‌توانید از سیستم خارج شوید. تور به پایان رسید، از استفاده از پورتال لذت ببرید!',
    position: 'left',
  },
];

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly STORAGE_PREFIX = 'afraTourCompleted_';

  private readonly stepPrepares = new Map<TourStepId, StepPrepare>();
  private readonly registeredSteps = new Map<TourStepId, RegisteredStep[]>();
  private readonly stepConfigs: ReadonlyMap<TourStepId, StepConfig> = new Map(
    TOUR_STEPS.map((step) => [step.name, step] as [TourStepId, StepConfig]),
  );

  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);

  private activeIndex = 0;
  private activeStepIds: TourStepId[] = [];

  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly totalSteps = signal(0);
  readonly currentIndex = signal(0);
  readonly isActive = signal(false);
  readonly currentStep = signal<TourStep | null>(null);

  get isMobile(): boolean {
    return window.innerWidth < 768;
  }

  registerStep(
    stepId: TourStepId,
    elementRef: ElementRef<HTMLElement>,
    popoverPosition?: TourPopoverPosition,
  ): void {
    const config = this.stepConfigs.get(stepId);

    if (!config) return;

    const steps = this.registeredSteps.get(stepId) || [];

    steps.push({
      elementRef,
      popoverPosition: popoverPosition ?? config.position,
    });

    this.registeredSteps.set(stepId, steps);
  }

  registerStepCallbacks(stepId: TourStepId, callbacks: StepPrepare): void {
    this.stepPrepares.set(stepId, {
      showDelay: callbacks.showDelay ?? 400,
      beforeShow: callbacks.beforeShow,
      afterHide: callbacks.afterHide,
    });
  }

  unregisterStep(
    stepId: TourStepId,
    elementRef: ElementRef<HTMLElement>,
  ): void {
    const steps = this.registeredSteps.get(stepId);

    if (!steps) return;

    const stepIndex = steps.findIndex((step) => step.elementRef === elementRef);

    if (stepIndex >= 0) steps.splice(stepIndex, 1);
    if (steps.length === 0) this.registeredSteps.delete(stepId);
  }

  private getStorageKey(): string {
    const user = this.authService.user.value;

    if (!user?.token) return `${this.STORAGE_PREFIX}anonymous`;

    try {
      const payload = JSON.parse(atob(user.token.split('.')[1]));
      const id = payload.user_id || payload.sub || payload.id || 'anonymous';

      return `${this.STORAGE_PREFIX}${id}`;
    } catch {
      return `${this.STORAGE_PREFIX}anonymous`;
    }
  }

  shouldShowTour(): boolean {
    return localStorage.getItem(this.getStorageKey()) !== 'true';
  }

  private getActiveStepIds(): TourStepId[] {
    return TOUR_STEPS.filter((step) => {
      if (!step.permissions?.length) return true;

      return this.permissionService.hasAnyPermission(step.permissions);
    })
      .map((step) => step.name)
      .filter((name) => this.registeredSteps.has(name));
  }

  private findVisibleStep(stepId: TourStepId): RegisteredStep | null {
    const steps = this.registeredSteps.get(stepId);

    if (!steps?.length) return null;

    return (
      steps.find((step) => {
        const rect = step.elementRef.nativeElement.getBoundingClientRect();

        return (
          rect.width > 0 &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top < window.innerHeight
        );
      }) ?? steps[0]
    );
  }

  private isVisible(step: RegisteredStep): boolean {
    const rect = step.elementRef.nativeElement.getBoundingClientRect();

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight
    );
  }

  private clearPending(): void {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);

      this.pendingTimeout = null;
    }
  }

  startTour(): void {
    // Wait one microtask after navigation so Angular finishes rendering
    // the home route before we query registered DOM elements.
    this.router
      .navigateByUrl('/')
      .then(() => setTimeout(() => this.initTour()));
  }

  private initTour(): void {
    const activeStepIds = this.getActiveStepIds();

    if (!activeStepIds.length) return;

    this.activeStepIds = activeStepIds;
    this.activeIndex = 0;

    this.totalSteps.set(activeStepIds.length);
    this.isActive.set(true);

    this.showStep();
  }

  showNextStep(): void {
    if (this.activeIndex < this.activeStepIds.length - 1) {
      this.callAfterHide(this.activeIndex + 1);
      this.activeIndex++;

      this.showStep();
    }
  }

  showPreviousStep(): void {
    if (this.activeIndex > 0) {
      this.callAfterHide(this.activeIndex - 1);
      this.activeIndex--;

      this.showStep();
    }
  }

  endTour(): void {
    this.callAfterHide();
    this.clearPending();

    this.isActive.set(false);
    this.currentStep.set(null);

    this.activeStepIds = [];

    localStorage.setItem(this.getStorageKey(), 'true');
  }

  private callAfterHide(nextIndex?: number): void {
    const stepId = this.activeStepIds[this.activeIndex];

    if (!stepId) return;

    const prepare = this.stepPrepares.get(stepId);

    if (!prepare?.afterHide) return;

    if (nextIndex !== undefined) {
      const nextStepId = this.activeStepIds[nextIndex];
      const nextPrepare = nextStepId
        ? this.stepPrepares.get(nextStepId)
        : undefined;

      if (prepare.beforeShow === nextPrepare?.beforeShow) return;
    }

    prepare.afterHide();
  }

  private showStep(): void {
    this.clearPending();

    const stepId = this.activeStepIds[this.activeIndex];
    const steps = this.registeredSteps.get(stepId) ?? [];
    const prepare = this.stepPrepares.get(stepId);

    if (!prepare) {
      this.tryShowStep(stepId);
      return;
    }

    const alreadyVisible = steps.some((step) => this.isVisible(step));

    if (alreadyVisible) {
      this.tryShowStep(stepId);
      return;
    }

    prepare.beforeShow();

    this.pendingTimeout = setTimeout(
      () => this.tryShowStep(stepId),
      prepare.showDelay,
    );
  }

  private tryShowStep(stepId: TourStepId, retry = 0): void {
    const config = this.stepConfigs.get(stepId);
    const registered = this.findVisibleStep(stepId);

    if (!config || !registered) {
      if (this.activeIndex < this.activeStepIds.length - 1) {
        this.activeIndex++;
        this.showStep();
      } else {
        this.endTour();
      }

      return;
    }

    const rect = registered.elementRef.nativeElement.getBoundingClientRect();

    if ((rect.width === 0 || rect.height === 0) && retry < 5) {
      this.pendingTimeout = setTimeout(
        () => this.tryShowStep(stepId, retry + 1),
        100,
      );

      return;
    }

    const popoverPosition =
      this.isMobile &&
      (registered.popoverPosition === 'left' ||
        registered.popoverPosition === 'right')
        ? 'top'
        : registered.popoverPosition;

    this.currentIndex.set(this.activeIndex);
    this.currentStep.set({
      elementRef: registered.elementRef,
      title: config.title,
      text: config.text,
      popoverPosition,
    });
  }
}
